import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import UseLangLstQuery from 'Apis/LangTxtInqApi';
import LnchStrtApi from 'Apis/LnchStrtApi';
import GameSvcLinkApi from 'Apis/GameSvcLinkApi';
import IsMntnInqApi from 'Apis/IsMntnInqApi';
import ApiResultModal from 'Components/ApiResultModal';
import GlitchImage from 'Utils/GlitchImage';
import GameLnchMdalUi from 'Utils/GameLnchMdalUi';
import useMaintenanceModalGuard from 'hooks/useMaintenanceModalGuard';

const parseKstDateToUtcMs = (value?: string): number | null => {
    if (!value) {
        return null;
    }

    const normalized = value.trim().replace(' ', 'T');
    if (!normalized) {
        return null;
    }

    const hasTimezone = /([zZ]|[+-]\d{2}:\d{2})$/.test(normalized);
    const kstIso = hasTimezone ? normalized : `${normalized}+09:00`;
    const parsed = Date.parse(kstIso);

    return Number.isNaN(parsed) ? null : parsed;
};

const resolveImageSrc = (pathValue: string) => {
    if (/^https?:\/\//i.test(pathValue)) {
        return pathValue;
    }

    const normalized = pathValue.startsWith('/') ? pathValue : `/${pathValue}`;
    return `${process.env.PUBLIC_URL}${normalized}`;
};

const MUSIC_OWNER_KEY = 'globalMusicOwnerTabId';
const MUSIC_TIME_KEY = 'globalMusicCurrentTime';
const MUSIC_USER_PREFERENCE = 'globalMusicUserPreference';
const MUSIC_FORCE_PAUSE_EVENT = 'global-music-force-pause';

const PreregisterPlayNowButton = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { data: langData }: any = UseLangLstQuery();
    const [mntnData, setMntnData] = useState<any[]>([]);
    const [isImgGlitch, setIsImgGlitch] = useState(false);
    const [showLaunchModal, setShowLaunchModal] = useState(false);
    const btnRef = useRef<HTMLButtonElement>(null);
    const modalRoot = typeof document !== 'undefined' ? document.body : null;

    const {
        isMaintenance,
        resultModalOpen,
        resultModalMessage,
        openResultModal,
        closeResultModal,
        runWithMaintenanceGuard,
    } = useMaintenanceModalGuard({
        mntnData,
        targetGameSvcCd: 'BBO',
        langData,
        specialModalStartKst: process.env.REACT_APP_PREREGISTER_MAINTENANCE_MODAL_START_KST,
        specialModalEndKst: process.env.REACT_APP_PREREGISTER_MAINTENANCE_MODAL_END_KST,
        specialModalMessage: '<h1 style="font-size:18px;">2026.08.27 (목) GRAND OPEN</h1><p>\n곧 새로운 모험이 시작됩니다!\n\nBE@RBRICK HEROES와 함께할\n모험을 준비 중이니 조금만 기다려 주세요!</p>',
    });

    const SPRITE_CONFIG = useMemo(() => ({
        buttonWidth: 240,
        buttonHeight: 60,
    }), []);

    const playNowButtonImageSrc = useMemo(() => {
        const switchStartKst = process.env.REACT_APP_PREREGISTER_MAINTENANCE_MODAL_START_KST;
        const switchEndKst = process.env.REACT_APP_PREREGISTER_MAINTENANCE_MODAL_END_KST;
        const switchStartUtcMs = parseKstDateToUtcMs(switchStartKst);
        const switchEndUtcMs = parseKstDateToUtcMs(switchEndKst);
        const beforePath = process.env.REACT_APP_PREREGISTER_PLAYNOW_BTN_IMG_BEFORE || '/images/evnt/grandopen/gameStrtBtn.png';
        const afterPath = process.env.REACT_APP_PREREGISTER_PLAYNOW_BTN_IMG_AFTER || '/images/evnt/grandopen/gameStrtBtn02.png';

        const now = Date.now();
        const isAfterStart = switchStartUtcMs !== null && now >= switchStartUtcMs;
        const isBeforeEnd = switchEndUtcMs === null || now < switchEndUtcMs;

        if (isAfterStart && isBeforeEnd) {
            return resolveImageSrc(afterPath);
        }

        return resolveImageSrc(beforePath);
    }, []);

    useEffect(() => {
        IsMntnInqApi('mgp')
            .then((resp) => {
                
                setMntnData(Array.isArray(resp?.apiBody) ? resp.apiBody : []);
            })
            .catch(() => {
                setMntnData([]);
            });
    }, []);

    const stopBackgroundMusic = () => {
        localStorage.setItem(MUSIC_USER_PREFERENCE, 'pause');

        const globalMusicAudio = document.querySelector('.music-player audio') as HTMLAudioElement | null;
        if (globalMusicAudio) {
            localStorage.setItem(MUSIC_TIME_KEY, `${globalMusicAudio.currentTime || 0}`);
            globalMusicAudio.pause();
        }

        localStorage.removeItem(MUSIC_OWNER_KEY);
        window.dispatchEvent(new Event(MUSIC_FORCE_PAUSE_EVENT));
    };

    const launchGame = async () => {
        try {
            const firstResp = await LnchStrtApi('BBO', null);

            switch (firstResp?.apiHdr?.code) {
                case 'GAME0000':
                    if (firstResp?.apiBody?.lnchScheme) {
                        stopBackgroundMusic();
                        window.location.href = firstResp.apiBody.lnchScheme;
                    }
                    break;
                case 'GAME3101': {
                    await GameSvcLinkApi('BBO');
                    const retryResp = await LnchStrtApi('BBO', null);
                    if (retryResp?.apiHdr?.code === 'GAME0000' && retryResp?.apiBody?.lnchScheme) {
                        stopBackgroundMusic();
                        window.location.href = retryResp.apiBody.lnchScheme;
                    } else {
                        openResultModal(retryResp?.apiHdr?.msg || '게임 실행에 실패했습니다.');
                    }
                    break;
                }
                case 'ATHN2303':
                    navigate(`/Login?referrer=${encodeURIComponent(location.pathname + location.search)}`);
                    break;
                default:
                    openResultModal(firstResp?.apiHdr?.msg || '게임 실행 중 오류가 발생했습니다.');
                    break;
            }

            // Keep the same launcher modal flow used in GameSubHdr.
            setShowLaunchModal(true);
        } catch (error: any) {
            if (error?.code === 'ATHN2303') {
                navigate(`/Login?referrer=${encodeURIComponent(location.pathname + location.search)}`);
                return;
            }
            openResultModal(langData?.GAME_LAUNCH_ERROR || '게임 실행 중 오류가 발생했습니다.');
        }
    };

    const handleClick = () => {
        runWithMaintenanceGuard(launchGame);
    };

    return (
        <>
            <ApiResultModal
                open={resultModalOpen}
                message={resultModalMessage}
                onClose={closeResultModal}
                confirmText={langData?.COMM_MAIN_CHK_MAIN_BTN_00 || '확인'}
            />
            {showLaunchModal && modalRoot && createPortal(
                <GameLnchMdalUi
                    gameMainPageInfoState={null}
                    onClose={() => setShowLaunchModal(false)}
                    langData={langData}
                />,
                modalRoot
            )}
            <button
                ref={btnRef}
                className="gameStartBtn colNoneBtn"
                onClick={handleClick}
                onMouseEnter={() => setIsImgGlitch(true)}
                onMouseLeave={() => setIsImgGlitch(false)}
                style={{
                    backgroundImage: 'url()',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: '0px 0px',
                    backgroundSize: `${SPRITE_CONFIG.buttonWidth}px auto`,
                    maxWidth: '240px',
                    width: `${SPRITE_CONFIG.buttonWidth}px`,
                    height: `${SPRITE_CONFIG.buttonHeight}px`,
                    minWidth: `${SPRITE_CONFIG.buttonWidth}px`,
                    minHeight: `${SPRITE_CONFIG.buttonHeight}px`,
                    transition: 'none',
                    position: 'relative',
                    right: '0',
                    filter: isMaintenance ? 'grayscale(100%)' : 'none',
                    cursor: isMaintenance ? 'not-allowed' : 'pointer',
                    opacity: isMaintenance ? 0.6 : 1,
                    overflow: 'hidden',
                } as React.CSSProperties}
            >
                <GlitchImage
                    src={playNowButtonImageSrc}
                    alt="Play Now"
                    width={SPRITE_CONFIG.buttonWidth}
                    height={SPRITE_CONFIG.buttonHeight}
                    isGlitch={isImgGlitch}
                />
            </button>
        </>
    );
};

export default PreregisterPlayNowButton;
